import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { Calendar, CalendarDateTemplateEvent } from 'primereact/calendar'
import { SplitButton } from 'primereact/splitbutton'
import { Button, ButtonProps } from 'primereact/button'

import { Row } from './pure/FlexContainer'
import { pi } from './pure/PrimeIcon'

interface CalendarActionWrapperProps extends PropsWithChildren {
    open: boolean
    value: Date | Date[]
    highlightedDates?: Date[]
    onHide: () => void
    onDateChange?: (value: Date) => void
    onRangeChange?: (value: Date[]) => void
}
export const CalendarActionWrapper = ({
    open,
    value,
    highlightedDates,
    onHide,
    onDateChange,
    onRangeChange,
    children,
}: CalendarActionWrapperProps) => {
    const ref = useRef<Calendar>(null)
    const useRange = Array.isArray(value)

    if (onDateChange && onRangeChange)
        console.warn(
            'CalendarActionWrapper received ambiguous change handlers. Using ' +
                (useRange ? 'date range' : 'single date')
        )

    useEffect(() => {
        const calendar = ref.current
        if (!calendar) return
        if (open) calendar.show()
        else calendar.hide()
    }, [open, ref.current])

    const onChange = (value?: Date | (Date | null)[]) => {
        if (useRange) onRangeChange?.(value as Date[])
        else onDateChange?.(value as Date)
    }

    const highlights =
        highlightedDates?.map((d) => ({
            day: d.getDate(),
            month: d.getMonth(),
            year: d.getFullYear(),
        })) ?? []

    const isHighlighted = (e: CalendarDateTemplateEvent) =>
        highlights.find((h) => h.day === e.day && h.month === e.month && h.year === e.year)

    return (
        <Row relative className="calendar-action">
            <Calendar
                ref={ref}
                onHide={onHide}
                onChange={(event) => onChange(event.value || undefined)}
                dateTemplate={(e) => (isHighlighted(e) ? <HighlightedDate day={e.day} /> : e.day)}
                value={value}
                selectionMode={useRange ? 'range' : 'single'}
                hideOnRangeSelection
                className="calendar-action__input"
                panelClassName="calendar-action__panel"
                showButtonBar
            />
            {children}
        </Row>
    )
}

interface CalendarButtonProps {
    buttonText: string
    value: Date | Date[]
    highlightedDates?: Date[]
    onClick: () => void
    onDateChange?: (value: Date) => void
    onRangeChange?: (value: Date[]) => void
}

export function SplitCalendarButton({
    buttonText,
    value,
    highlightedDates,
    onClick,
    onDateChange,
    onRangeChange,
}: CalendarButtonProps) {
    const [open, setOpen] = useState(false)

    return (
        <CalendarActionWrapper
            value={value}
            open={open}
            onHide={() => setOpen(false)}
            onDateChange={onDateChange}
            onRangeChange={onRangeChange}
            highlightedDates={highlightedDates}
        >
            <SplitButton
                outlined
                severity="secondary"
                className="calendar-action__split-button"
                menuButtonProps={{ onClick: () => setOpen(true) }}
                label={buttonText}
                onClick={onClick}
            />
        </CalendarActionWrapper>
    )
}

const RoundCalendarButton = ({ onClick }: ButtonProps) => (
    <Button
        className="calendar-action__round-button"
        onClick={onClick}
        icon={pi('calendar')}
        severity="secondary"
        size="small"
        rounded
        outlined
    />
)

interface HighlightedDateProps {
    day: number
}
const HighlightedDate = ({ day }: HighlightedDateProps) => (
    <div className="date-highlight">{day}</div>
)

interface CalendarRangeButtonProps extends Omit<CalendarButtonProps, 'onDateChange'> {}
interface RoundCalendarRangeButton
    extends Pick<CalendarRangeButtonProps, 'value' | 'onRangeChange' | 'highlightedDates'> {}
export const RoundCalendarRangeButton = ({
    value,
    onRangeChange,
    highlightedDates,
}: RoundCalendarRangeButton) => {
    const [open, setOpen] = useState(false)

    const change = (value: Date[]) => {
        onRangeChange?.(value)
        if (value.filter((d) => !!d).length > 1) setOpen(false)
    }

    return (
        <CalendarActionWrapper
            open={open}
            value={value}
            onHide={() => setOpen(false)}
            onRangeChange={change}
            highlightedDates={highlightedDates}
        >
            <RoundCalendarButton onClick={() => setOpen(!open)} />
        </CalendarActionWrapper>
    )
}

interface CalendarDateButtonProps extends Omit<CalendarButtonProps, 'onRangeChange'> {}
interface RoundCalendarDateButton
    extends Pick<CalendarDateButtonProps, 'value' | 'onDateChange' | 'highlightedDates'> {}
export const RoundCalendarDateButton = ({
    value,
    onDateChange,
    highlightedDates,
}: RoundCalendarDateButton) => {
    const [open, setOpen] = useState(false)

    const change = (value: Date) => {
        if (!value) return
        onDateChange?.(value)
        setOpen(false)
    }

    return (
        <CalendarActionWrapper
            open={open}
            value={value}
            onHide={() => setOpen(false)}
            onDateChange={change}
            highlightedDates={highlightedDates}
        >
            <RoundCalendarButton onClick={() => setOpen(!open)} />
        </CalendarActionWrapper>
    )
}
